import React, { useState, useEffect, useCallback } from 'react';
import { reviewsAPI, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Star, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VoiceInputButton } from './VoiceInputButton';

function StarRating({ rating, onRate, interactive = false, size = 'w-5 h-5' }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`${size} ${interactive ? 'cursor-pointer' : ''} ${i <= rating ? 'text-[#F59E0B]' : 'text-[#333333]'}`}
          weight={i <= rating ? 'fill' : 'regular'}
          onClick={() => interactive && onRate?.(i)}
          data-testid={`star-${i}`}
        />
      ))}
    </div>
  );
}

export function ReviewsSummary({ avgRating = 0, totalReviews = 0 }) {
  if (totalReviews === 0) return null;
  return (
    <div className="flex items-center gap-2" data-testid="reviews-summary">
      <StarRating rating={Math.round(avgRating)} size="w-4 h-4" />
      <span className="text-sm text-[#999999]">{avgRating} ({totalReviews})</span>
    </div>
  );
}

export default function ReviewsSection({ productId, sellerId }) {
  const { user, isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [newRating, setNewRating] = useState(0);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await reviewsAPI.getForProduct(productId);
      setReviews(data.reviews || []);
      setAvgRating(data.avg_rating || 0);
      setTotalReviews(data.total_reviews || 0);
    } catch (err) { console.error('Failed to load reviews:', err); }
  }, [productId]);

  useEffect(() => { loadReviews(); }, [productId, loadReviews]);

  const handleSubmit = async () => {
    if (newRating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await reviewsAPI.create(productId, newRating, newText);
      toast.success('Review submitted!');
      setNewRating(0);
      setNewText('');
      setShowForm(false);
      loadReviews();
    } catch (err) { toast.error(formatApiError(err)); }
    setSubmitting(false);
  };

  const handleDelete = async (reviewId) => {
    try {
      await reviewsAPI.delete(reviewId);
      toast.success('Review deleted');
      loadReviews();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const hasReviewed = reviews.some(r => r.user_id === user?.user_id);
  const isOwner = sellerId === user?.user_id;
  const canReview = isAuthenticated && !hasReviewed && !isOwner;

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (diff < 86400) return 'today';
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(d).toLocaleDateString();
  };

  return (
    <div className="mt-6" data-testid="reviews-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-[#111111]" style={{ fontFamily: 'Cabinet Grotesk' }}>Reviews</h3>
          {totalReviews > 0 && (
            <div className="flex items-center gap-1.5">
              <StarRating rating={Math.round(avgRating)} size="w-4 h-4" />
              <span className="text-sm font-medium text-[#555555]">{avgRating}</span>
              <span className="text-sm text-[#999999]">({totalReviews})</span>
            </div>
          )}
        </div>
        {canReview && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            variant="outline"
            className="text-sm border-[#E5E5E5] hover:bg-[#F5F5F7] rounded-sm"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        )}
      </div>

      {/* Write Review Form */}
      {showForm && (
        <Card className="mb-4 border-[#E5E5E5] rounded-sm shadow-none bg-[#FAFAFA]">
          <CardContent className="p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-[#555555] mb-1">Your Rating</p>
              <StarRating rating={newRating} onRate={setNewRating} interactive size="w-6 h-6" />
            </div>
            <div className="relative">
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Share your experience (optional)"
                className="w-full bg-white border border-[#E5E5E5] rounded-sm p-3 text-sm text-[#111111] placeholder:text-[#999999] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#22C55E]"
                data-testid="review-text-input"
              />
              <div className="absolute bottom-2 right-2">
                <VoiceInputButton onTranscript={(text) => setNewText(prev => prev ? prev + ' ' + text : text)} className="bg-white border-[#E5E5E5] text-[#999999] hover:text-[#22C55E] hover:border-[#22C55E]" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSubmit}
                disabled={submitting || newRating === 0}
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-sm"
                data-testid="submit-review-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </Button>
              <Button
                onClick={() => { setShowForm(false); setNewRating(0); setNewText(''); }}
                variant="ghost"
                className="text-[#999999] text-sm"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <p className="text-sm text-[#999999] py-4">No reviews yet. Be the first to review!</p>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => (
            <div key={r.review_id} className="border-b border-[#F0F0F0] pb-3 last:border-0" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {r.user_avatar ? (
                    <img src={r.user_avatar} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center text-[#22C55E] text-[10px] font-bold">
                      {r.user_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span className="text-sm font-medium text-[#111111]">{r.user_name}</span>
                  <span className="text-xs text-[#999999]">{timeAgo(r.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating rating={r.rating} size="w-3.5 h-3.5" />
                  {(r.user_id === user?.user_id || user?.role === 'admin') && (
                    <button onClick={() => handleDelete(r.review_id)} className="text-[#999999] hover:text-[#EF4444] transition-colors" data-testid={`delete-review-${r.review_id}`}>
                      <Trash className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {r.text && <p className="text-sm text-[#555555] pl-8">{r.text}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
